import React, { useEffect, useRef, useState } from "react";
import styles from "./EnveloppeTable.module.scss";

const STATUTS = [
  { value: 1, label: "Brouillon", obligatoire: true },
  { value: 2, label: "Demande envoyée" },
  { value: 3, label: "Accord reçu" },
  { value: 4, label: "Dossier complet" },
  { value: 5, label: "Dossier transmis" },
  { value: 6, label: "Transmis et signé", obligatoire: true },
];

/**
 * Panneau de configuration étapes actives / todos par étape, partagé entre
 * le gabarit financeur (étapes/todos par défaut) et la configuration d'une
 * enveloppe, qui peut le surcharger.
 *
 * Blocs unifiés par étape : chaque étape affiche son activation, ses todos
 * (réordre, édition inline, retrait) et un champ d'ajout inline. Chaque
 * action est enregistrée immédiatement (autosave) ; les contrôles sont
 * désactivés pendant l'enregistrement (les PUT todos sont des synchros
 * complètes, on les sérialise).
 *
 * Props :
 * - etapesActives, todos : configuration actuelle (source de vérité serveur).
 * - locked : désactive l'édition des étapes (todos restent toujours éditables).
 * - lockedHint : message affiché quand locked=true.
 * - banner : contenu additionnel affiché en tête du panneau (ex. mention héritage).
 * - confirmFirstSave : message de confirmation exigé avant toute mutation
 *   (enveloppe suivant encore le gabarit financeur : la première sauvegarde
 *   la détache définitivement). null = pas de confirmation.
 * - onSaveEtapes(etapes) / onSaveTodos(todos) : Promise<bool> — false = échec,
 *   l'état local est restauré depuis les props.
 */
const EtapesTodosConfigForm = ({
  etapesActives,
  todos,
  locked = false,
  lockedHint = null,
  banner = null,
  confirmFirstSave = null,
  onSaveEtapes,
  onSaveTodos,
}) => {
  const [etapes, setEtapes] = useState(etapesActives);
  const [todosState, setTodosState] = useState(todos);
  const [pending, setPending] = useState({}); // saisie d'ajout par étape
  const [editing, setEditing] = useState(null); // { index, libelle }
  // Ferme la session d'édition de façon déterministe : le blur déclenché par
  // le démontage de l'input (après Entrée ou Échap) retrouve cette ref à
  // true et ne doit ni recommettre, ni committer une édition annulée.
  const editionFermee = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef(null);

  // Resynchronise l'état local après un rechargement (sauvegarde réussie ou
  // changement de source — enveloppe sélectionnée, gabarit financeur).
  useEffect(() => {
    setEtapes(etapesActives);
    setTodosState(todos);
    setEditing(null);
    editionFermee.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapesActives, todos]);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  // Tant que l'enveloppe suit le gabarit financeur, la première sauvegarde
  // la détache définitivement : on exige une confirmation explicite. Après
  // succès, le parent recharge et confirmFirstSave repasse à null.
  const confirmMutation = () =>
    !confirmFirstSave || window.confirm(confirmFirstSave);

  const flashSaved = () => {
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2500);
  };

  const rollback = () => {
    setEtapes(etapesActives);
    setTodosState(todos);
  };

  const toggleEtape = async (statut, obligatoire) => {
    if (obligatoire || locked || saving) return;
    if (!confirmMutation()) return;
    const next = etapes.includes(statut)
      ? etapes.filter((s) => s !== statut)
      : [...etapes, statut].sort((a, b) => a - b);
    setEtapes(next);
    setSaving(true);
    const ok = await onSaveEtapes(next);
    setSaving(false);
    if (ok === false) rollback();
    else flashSaved();
  };

  // L'ordre est scindé par étape (cf. tri de la checklist demande) : il suit
  // l'ordre d'apparition dans le tableau parmi les todos de la même étape.
  const normaliserOrdres = (liste) => {
    const parStatut = {};
    return liste.map((t) => {
      parStatut[t.statut] = (parStatut[t.statut] ?? -1) + 1;
      return { ...t, ordre: parStatut[t.statut] };
    });
  };

  const mutateTodos = async (updater) => {
    if (saving) return;
    const next = updater(todosState);
    if (next === todosState) return;
    if (!confirmMutation()) return;
    const normalises = normaliserOrdres(next);
    setTodosState(normalises);
    setSaving(true);
    const ok = await onSaveTodos(normalises);
    setSaving(false);
    if (ok === false) rollback();
    else flashSaved();
  };

  const addTodo = (statut) => {
    const libelle = (pending[statut] || "").trim();
    if (!libelle) return;
    setPending((p) => ({ ...p, [statut]: "" }));
    mutateTodos((prev) => [
      ...prev,
      { uuid: null, statut, libelle, ordre: 0 },
    ]);
  };

  const removeTodo = (index) => {
    mutateTodos((prev) => prev.filter((_, i) => i !== index));
  };

  // Échange avec le todo voisin (direction -1 = monter, +1 = descendre) au
  // sein de la même étape, dans le tableau à plat.
  const moveTodo = (index, direction) => {
    mutateTodos((prev) => {
      const statut = prev[index].statut;
      const positions = prev
        .map((t, i) => ({ statut: t.statut, i }))
        .filter((x) => x.statut === statut)
        .map((x) => x.i);
      const pos = positions.indexOf(index);
      const cible = positions[pos + direction];
      if (cible === undefined) return prev;
      const next = [...prev];
      [next[index], next[cible]] = [next[cible], next[index]];
      return next;
    });
  };

  const startEdit = (index, libelle) => {
    if (saving) return;
    editionFermee.current = false;
    setEditing({ index, libelle });
  };

  const cancelEdit = () => {
    editionFermee.current = true;
    setEditing(null);
  };

  const commitEdit = () => {
    if (!editing || editionFermee.current) return;
    editionFermee.current = true;
    const { index, libelle } = editing;
    const propre = libelle.trim();
    setEditing(null);
    if (!propre || propre === todosState[index].libelle) return;
    mutateTodos((prev) =>
      prev.map((t, i) => (i === index ? { ...t, libelle: propre } : t)),
    );
  };

  return (
    <div className={styles.configPanel}>
      {banner}
      {locked && lockedHint && <div className={styles.hint}>{lockedHint}</div>}
      <div className={styles.etapeBlocks}>
        {STATUTS.map((s) => {
          const active = etapes.includes(s.value);
          const todosEtape = todosState
            .map((t, i) => ({ ...t, index: i }))
            .filter((t) => t.statut === s.value);
          return (
            <section
              key={s.value}
              className={`${styles.etapeBlock} ${
                active ? "" : styles.etapeBlockInactive
              }`}
            >
              <label className={styles.etapeBlockHead}>
                {/* Pas de .form-check-input : hors d'un conteneur .form-check,
                    Bootstrap 4 la positionne en absolute hors du flux flex. */}
                <input
                  type="checkbox"
                  checked={active}
                  disabled={s.obligatoire || locked || saving}
                  onChange={() => toggleEtape(s.value, s.obligatoire)}
                />
                <span>{s.label}</span>
                {s.obligatoire && (
                  <span className={styles.etapeBadge}>
                    <i className="fa fa-lock" /> obligatoire
                  </span>
                )}
              </label>

              {active ? (
                <div className={styles.etapeBlockBody}>
                  {todosEtape.length > 0 && (
                    <ul className={styles.todoList}>
                      {todosEtape.map((t, pos) => (
                        <li key={t.uuid || `new-${t.index}`}>
                          {editing?.index === t.index ? (
                            <input
                              autoFocus
                              className={styles.input}
                              value={editing.libelle}
                              onChange={(e) =>
                                setEditing((p) => ({
                                  ...p,
                                  libelle: e.target.value,
                                }))
                              }
                              onBlur={commitEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitEdit();
                                if (e.key === "Escape") cancelEdit();
                              }}
                            />
                          ) : (
                            <button
                              type="button"
                              className={styles.todoLabel}
                              onClick={() => startEdit(t.index, t.libelle)}
                              title="Modifier le libellé"
                            >
                              {t.libelle}
                            </button>
                          )}
                          <span className={styles.todoActions}>
                            <button
                              type="button"
                              className={styles.btnIcon}
                              disabled={pos === 0 || saving}
                              onClick={() => moveTodo(t.index, -1)}
                              aria-label={`Monter le todo ${t.libelle}`}
                            >
                              <i className="fa fa-chevron-up" />
                            </button>
                            <button
                              type="button"
                              className={styles.btnIcon}
                              disabled={
                                pos === todosEtape.length - 1 || saving
                              }
                              onClick={() => moveTodo(t.index, 1)}
                              aria-label={`Descendre le todo ${t.libelle}`}
                            >
                              <i className="fa fa-chevron-down" />
                            </button>
                            <button
                              type="button"
                              className={styles.btnIconDanger}
                              disabled={saving}
                              onClick={() => removeTodo(t.index)}
                              aria-label={`Retirer le todo ${t.libelle}`}
                            >
                              <i className="fa fa-times" />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className={styles.todoAdd}>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Ajouter un todo…"
                      value={pending[s.value] || ""}
                      disabled={saving}
                      onChange={(e) =>
                        setPending((p) => ({
                          ...p,
                          [s.value]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => e.key === "Enter" && addTodo(s.value)}
                    />
                    <button
                      type="button"
                      className={styles.btnIcon}
                      disabled={saving || !(pending[s.value] || "").trim()}
                      onClick={() => addTodo(s.value)}
                      aria-label={`Ajouter un todo à l'étape ${s.label}`}
                    >
                      <i className="fa fa-plus" />
                    </button>
                  </div>
                </div>
              ) : (
                todosEtape.length > 0 && (
                  <div className={styles.etapeBlockKept}>
                    {todosEtape.length} todo
                    {todosEtape.length > 1 ? "s" : ""} conservé
                    {todosEtape.length > 1 ? "s" : ""} — réactivez l'étape
                    pour les retrouver
                  </div>
                )
              )}
            </section>
          );
        })}
      </div>
      <div className={styles.saveIndicator} role="status" aria-live="polite">
        {saving ? (
          <>
            <i className="fa fa-circle-o-notch fa-spin" /> Enregistrement…
          </>
        ) : saved ? (
          <>
            <i className="fa fa-check" /> Enregistré
          </>
        ) : null}
      </div>
    </div>
  );
};

export default EtapesTodosConfigForm;
